import {
    COMBINED_LENGTH_MESSAGE,
    DELIVERY_INSTRUCTIONS_LABELS,
    DELIVERY_INSTRUCTIONS_MAX_ENTRY_CODE_LENGTH,
    DELIVERY_INSTRUCTIONS_MAX_NOTES_LENGTH,
    DELIVERY_INSTRUCTIONS_MAX_SERIALIZED_LENGTH,
    DELIVERY_INSTRUCTIONS_RESERVED_TOKEN,
    DELIVERY_INSTRUCTIONS_RESERVED_TOKEN_MESSAGE,
    DELIVERY_INSTRUCTIONS_SEPARATOR,
    DELIVERY_LOCATIONS,
    ENTRY_CODE_LENGTH_MESSAGE,
    INVALID_LOCATION_MESSAGE,
    INVALID_TYPE_MESSAGE,
    NOTES_LENGTH_MESSAGE,
    UNSUPPORTED_CHARACTERS_MESSAGE,
} from '../constants/deliveryInstructions';
import type {
    DeliveryInstructions,
    DeliveryInstructionsInput,
    DeliveryInstructionsUpdate,
    DeliveryInstructionsValidationErrors,
    DeliveryLocation,
    NormalizedDeliveryInstructions,
    ParsedDeliveryInstructions,
} from '../types/deliveryInstructions';
import { isString } from './verifyTypes';

const fieldNames = ['deliveryLocation', 'entryCode', 'deliveryNotes'] as const;
const printableAsciiPattern = /^[\x20-\x7E]*$/;
const deliveryInstructionsSanitizationPattern = /[^\w\s~!@#$%^&*()+`\-={}|[\]\\:";'<>?,./À-ÿ]+/g;
const POSITIONAL_FIELD_COUNT = 3;

type TextFieldName = 'entryCode' | 'deliveryNotes';
type TextFieldRule = Readonly<{
    maxLength: number;
    lengthMessage: string;
}>;

const TEXT_FIELD_RULES: ReadonlyArray<readonly [TextFieldName, TextFieldRule]> = [
    ['entryCode', { maxLength: DELIVERY_INSTRUCTIONS_MAX_ENTRY_CODE_LENGTH, lengthMessage: ENTRY_CODE_LENGTH_MESSAGE }],
    ['deliveryNotes', { maxLength: DELIVERY_INSTRUCTIONS_MAX_NOTES_LENGTH, lengthMessage: NOTES_LENGTH_MESSAGE }],
];

const hasInputFields = (input: DeliveryInstructionsInput) => fieldNames.some(field => Object.hasOwn(input, field));
const isDeliveryLocation = (value: string): value is DeliveryLocation =>
    (DELIVERY_LOCATIONS as readonly string[]).includes(value);
const normalizeText = (value: unknown): string | null => (isString(value) ? value.trim() || null : null);
export const hasReservedDeliveryInstructionsToken = (value: string): boolean =>
    value.includes(DELIVERY_INSTRUCTIONS_RESERVED_TOKEN);

const isSerializableDeliveryLocation = (
    location: DeliveryLocation | null
): location is Exclude<DeliveryLocation, 'Other' | 'No Preference'> =>
    location !== null && location !== 'Other' && location !== 'No Preference';

const getSerializableLocation = (
    location: string | null
): Exclude<DeliveryLocation, 'Other' | 'No Preference'> | null =>
    location && isDeliveryLocation(location) && isSerializableDeliveryLocation(location) ? location : null;

const buildSerializedValue = (input: NormalizedDeliveryInstructions): string | null => {
    const locationSlot = getSerializableLocation(input.deliveryLocation) ?? '';
    const codeSlot = input.entryCode ?? '';
    const notesSlot = input.deliveryNotes ?? '';

    if (!locationSlot && !codeSlot && !notesSlot) {
        return null;
    }

    return [locationSlot, codeSlot, notesSlot].join(DELIVERY_INSTRUCTIONS_SEPARATOR);
};

const buildDownstreamLabeledValue = (input: NormalizedDeliveryInstructions): string | null => {
    const serializableLocation = getSerializableLocation(input.deliveryLocation);
    const parts = [
        serializableLocation && `${DELIVERY_INSTRUCTIONS_LABELS.location}: ${serializableLocation}`,
        input.entryCode && `${DELIVERY_INSTRUCTIONS_LABELS.code}: ${input.entryCode}`,
        input.deliveryNotes && `${DELIVERY_INSTRUCTIONS_LABELS.notes}: ${input.deliveryNotes}`,
    ].filter((part): part is string => Boolean(part));

    return parts.length > 0 ? parts.join(', ') : null;
};

const sanitizeLegacyValue = (value: string): string | undefined => {
    const sanitizedValue = value.replaceAll(deliveryInstructionsSanitizationPattern, '');

    return sanitizedValue.trim() ? sanitizedValue : undefined;
};

export const formatDeliveryInstructionsForDownstream = (value: string | null | undefined): string | undefined => {
    if (!isString(value)) {
        return undefined;
    }

    const parsedValue = parseDeliveryInstructions(value);
    switch (parsedValue.kind) {
        case 'structured':
            return buildDownstreamLabeledValue(parsedValue.instructions) ?? undefined;
        case 'legacy':
            return sanitizeLegacyValue(parsedValue.rawValue);
        case 'empty':
        case 'malformed':
            return undefined;
        default: {
            const _exhaustive: never = parsedValue;
            return _exhaustive;
        }
    }
};

export const formatDeliveryInstructionsPreview = (input: DeliveryInstructionsInput, maxLength?: number): string => {
    const preview = fieldNames
        .map(field => normalizeText(input[field]))
        .filter((value): value is string => value !== null)
        .join(', ');

    if (maxLength === undefined || preview.length <= maxLength) {
        return preview;
    }

    return `${preview.slice(0, maxLength).trimEnd()}...`;
};

const normalizeDeliveryInstructions = (input: DeliveryInstructionsInput): NormalizedDeliveryInstructions => ({
    deliveryLocation: normalizeText(input.deliveryLocation),
    entryCode: normalizeText(input.entryCode),
    deliveryNotes: normalizeText(input.deliveryNotes),
});

const validateDeliveryLocation = (value: unknown, normalizedValue: string | null): string | undefined => {
    if (value === undefined || value === null) {
        return undefined;
    }
    if (!isString(value)) {
        return INVALID_TYPE_MESSAGE;
    }
    if (normalizedValue === null) {
        return undefined;
    }
    if (!isDeliveryLocation(normalizedValue)) {
        return INVALID_LOCATION_MESSAGE;
    }
    return undefined;
};

const validateTextField = (
    value: unknown,
    normalizedValue: string | null,
    { maxLength, lengthMessage }: TextFieldRule
): string | undefined => {
    if (value === undefined || value === null) {
        return undefined;
    }
    if (!isString(value)) {
        return INVALID_TYPE_MESSAGE;
    }
    if (normalizedValue === null) {
        return undefined;
    }
    if (normalizedValue.length > maxLength) {
        return lengthMessage;
    }
    if (!printableAsciiPattern.test(normalizedValue)) {
        return UNSUPPORTED_CHARACTERS_MESSAGE;
    }
    if (hasReservedDeliveryInstructionsToken(normalizedValue)) {
        return DELIVERY_INSTRUCTIONS_RESERVED_TOKEN_MESSAGE;
    }
    return undefined;
};

const applyCombinedLengthErrors = (
    input: NormalizedDeliveryInstructions,
    errors: DeliveryInstructionsValidationErrors
): void => {
    if (Object.keys(errors).length > 0) {
        return;
    }

    const downstreamValue = buildDownstreamLabeledValue(input);
    if (!downstreamValue || downstreamValue.length <= DELIVERY_INSTRUCTIONS_MAX_SERIALIZED_LENGTH) {
        return;
    }
    errors.deliveryInstructions = COMBINED_LENGTH_MESSAGE;
};

export const validateDeliveryInstructions = (
    input: DeliveryInstructionsInput
): DeliveryInstructionsValidationErrors => {
    const errors: DeliveryInstructionsValidationErrors = {};
    const { deliveryLocation, entryCode, deliveryNotes } = input;
    const normalizedInput = normalizeDeliveryInstructions(input);
    const textFieldValues: Record<TextFieldName, unknown> = { entryCode, deliveryNotes };
    const normalizedTextFieldValues: Record<TextFieldName, string | null> = {
        entryCode: normalizedInput.entryCode,
        deliveryNotes: normalizedInput.deliveryNotes,
    };

    const locationError = validateDeliveryLocation(deliveryLocation, normalizedInput.deliveryLocation);
    if (locationError) {
        errors.deliveryLocation = locationError;
    }

    for (const [field, rule] of TEXT_FIELD_RULES) {
        const fieldError = validateTextField(textFieldValues[field], normalizedTextFieldValues[field], rule);
        if (fieldError) {
            errors[field] = fieldError;
        }
    }

    applyCombinedLengthErrors(normalizedInput, errors);

    return errors;
};

export const buildDeliveryInstructionsUpdate = (input: DeliveryInstructionsInput): DeliveryInstructionsUpdate => {
    if (!hasInputFields(input)) {
        return { kind: 'preserve' };
    }

    if (Object.keys(validateDeliveryInstructions(input)).length > 0) {
        throw new TypeError('Invalid delivery instructions.');
    }

    const serializedValue = buildSerializedValue(normalizeDeliveryInstructions(input));
    return serializedValue === null ? { kind: 'clear' } : { kind: 'replace', value: serializedValue };
};

const asLegacyDeliveryInstructions = (value: string): DeliveryInstructions => ({
    deliveryLocation: null,
    entryCode: null,
    deliveryNotes: value,
});

const createEmptyDeliveryInstructions = (): DeliveryInstructions => ({
    deliveryLocation: null,
    entryCode: null,
    deliveryNotes: null,
});

type DeliveryInstructionsLabel = (typeof DELIVERY_INSTRUCTIONS_LABELS)[keyof typeof DELIVERY_INSTRUCTIONS_LABELS];
type StructuredDeliveryInstructionsPart = {
    label: DeliveryInstructionsLabel;
    value: string;
};

const parseStructuredPart = (part: string): StructuredDeliveryInstructionsPart | null => {
    const label = Object.values(DELIVERY_INSTRUCTIONS_LABELS).find(candidate => part.startsWith(`${candidate}: `));
    if (!label) {
        return null;
    }

    const value = part.slice(label.length + 2);
    const trimmedValue = value.trim();
    if (!trimmedValue || trimmedValue !== value) {
        return null;
    }

    return { label, value };
};

const applyStructuredPart = (
    result: DeliveryInstructions,
    { label, value }: StructuredDeliveryInstructionsPart
): boolean => {
    if (label === DELIVERY_INSTRUCTIONS_LABELS.location) {
        if (!isDeliveryLocation(value)) {
            return false;
        }

        result.deliveryLocation = value;
    } else if (label === DELIVERY_INSTRUCTIONS_LABELS.code) {
        result.entryCode = value;
    } else {
        result.deliveryNotes = value;
    }

    return true;
};

const parseLabeledDeliveryInstructions = (value: string): DeliveryInstructions | null => {
    if (hasReservedDeliveryInstructionsToken(value) && !value.includes(DELIVERY_INSTRUCTIONS_SEPARATOR)) {
        return null;
    }

    const result = createEmptyDeliveryInstructions();
    const seenLabels = new Set<DeliveryInstructionsLabel>();

    for (const part of value.split(DELIVERY_INSTRUCTIONS_SEPARATOR)) {
        const structuredPart = parseStructuredPart(part);
        if (!structuredPart || seenLabels.has(structuredPart.label)) {
            return null;
        }
        if (!applyStructuredPart(result, structuredPart)) {
            return null;
        }

        seenLabels.add(structuredPart.label);
    }

    if (seenLabels.size === 0 || Object.keys(validateDeliveryInstructions(result)).length > 0) {
        return null;
    }

    return result;
};

const parsePositionalDeliveryInstructions = (value: string): DeliveryInstructions | null => {
    if (!hasReservedDeliveryInstructionsToken(value)) {
        return null;
    }

    const parts = value.split(DELIVERY_INSTRUCTIONS_RESERVED_TOKEN);
    if (parts.length !== POSITIONAL_FIELD_COUNT) {
        return null;
    }

    const [rawLocation, rawCode, rawNotes] = parts;
    const deliveryLocation = normalizeText(rawLocation);
    const entryCode = normalizeText(rawCode);
    const deliveryNotes = normalizeText(rawNotes);

    if (deliveryLocation !== null && !isDeliveryLocation(deliveryLocation)) {
        return null;
    }

    if (deliveryLocation === null && entryCode === null && deliveryNotes === null) {
        return null;
    }

    const result = {
        deliveryLocation,
        entryCode,
        deliveryNotes,
    };

    return Object.keys(validateDeliveryInstructions(result)).length === 0 ? result : null;
};

export const parseDeliveryInstructions = (value: string | null | undefined): ParsedDeliveryInstructions => {
    if (!isString(value) || !value.trim()) {
        return { kind: 'empty', instructions: createEmptyDeliveryInstructions() };
    }

    const normalizedValue = value.trim();
    const instructions =
        parseLabeledDeliveryInstructions(normalizedValue) ?? parsePositionalDeliveryInstructions(normalizedValue);

    if (instructions) {
        return { kind: 'structured', instructions };
    }

    if (hasReservedDeliveryInstructionsToken(normalizedValue)) {
        return { kind: 'malformed', instructions: createEmptyDeliveryInstructions() };
    }

    return {
        kind: 'legacy',
        instructions: asLegacyDeliveryInstructions(normalizedValue),
        rawValue: normalizedValue,
    };
};
